import React, { FC } from 'react';
import { Link, useRouteMatch } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Card, CardBody, CardHeader, Col, Row } from 'reactstrap';
import { HotelReservation, ReleaseGroupEntryExtended } from '../../../../shared/hotel';
import { ActionButtonModal, DateT } from '../../../components';
import { useTranslation } from '../../../translations';
import { capitalize, displayName, Fetcher, renderName, useFetcher } from '../../../utils';

export const ViewUserReservation: FC = () => {
  const match = useRouteMatch<{ id: string }>();
  const { ts } = useTranslation();

  const reservationFetcher = useFetcher(async () => {
    return await api.getHotelUserSummary(match.params.id);
  });

  if (!reservationFetcher.success) {
    return <Fetcher result={reservationFetcher} />;
  }

  const summary = reservationFetcher.data;
  const reservationsWithoutEntry = summary.reservations.filter(
    (r) => !summary.entries.some((e) => e.reservationId === r.id),
  );

  return (
    <Row>
      <Col md={12} xs={12}>
        {ts('summary_for')}{' '}
        <Link to={`/housekeeping/attendees/user/${summary.user.id}`}>
          {renderName(summary.user)}
        </Link>
      </Col>
      {summary.entries.map((entry) => {
        const reservation = summary.reservations.find((r) => r.id === entry.reservationId);

        return (
          <Col key={entry.id} md={6} xs={12}>
            <Card>
              <CardHeader>
                {ts('release_group')}{' '}
                <Link to={`/housekeeping/hotel/groups/${entry.releaseGroup.id}/view`}>
                  {' '}
                  {displayName(entry.releaseGroup)}
                </Link>
              </CardHeader>
              <CardBody>
                <ReleaseEntrySummary entry={entry} reload={reservationFetcher.refresh} />
                {reservation && <ReservationSummary reservation={reservation} />}
              </CardBody>
            </Card>
          </Col>
        );
      })}
      {reservationsWithoutEntry.map((reservation) => {
        return (
          <Col key={reservation.id} md={6} xs={12}>
            <Card>
              <CardHeader>
                {ts('reservation')}: {reservation.id} (Without Release Group Entry)
              </CardHeader>
              <CardBody>
                <ReservationSummary reservation={reservation} />
              </CardBody>
            </Card>
          </Col>
        );
      })}
    </Row>
  );
};

interface ReleaseEntrySummaryProps {
  readonly entry: ReleaseGroupEntryExtended;
  readonly reload: () => void;
}

const ReleaseEntrySummary: FC<ReleaseEntrySummaryProps> = ({ entry, reload }) => {
  const { ts } = useTranslation();
  return (
    <div>
      <ActionButtonModal
        actionContent="common.delete"
        buttonContent="common.delete"
        className="float-end"
        color="danger"
        id={`deleteEntry${entry.id}`}
        onComplete={async () => {
          await api.deleteReleaseEntry(entry.id);
          toast.success(ts('entry_deleted'));
          reload();
        }}
        title="common.delete_entry"
      >
        {ts(
          'are_you_sure_you_want_to_delete_this_entry_this_will_also_delete_the_associated_reservation',
        )}
      </ActionButtonModal>

      <div>
        <b>{ts('common.selected')}</b>: {entry.isSelected ? 'Yes' : 'No'}
      </div>
      <div>
        <b>{ts('email_sent')}</b>: {entry.isNotified ? 'Yes' : 'No'}
      </div>
      {entry.reservationTask && (
        <div>
          <b>{ts('reservation_task')}</b>: {capitalize(entry.reservationTask.status)} (
          <Link to={`/housekeeping/auditlog/Task/${entry.reservationTask.id}`}>
            {ts('link_to_logs')}
          </Link>
          ){' '}
        </div>
      )}
      {entry.disqualification && (
        <div>
          <b>{ts('disqualification')}</b>: {JSON.stringify(entry.disqualification)}
        </div>
      )}
      <div>
        <b>{ts('eligibility')}</b>: {entry.eligibility.isEligible ? 'Eligible' : 'Not eligible'}
        <ul>
          <li>
            {ts('common.product')}: {entry.eligibility.meetsProduct ? 'Yes' : 'No'}
          </li>
          <li>
            {ts('role')}: {entry.eligibility.meetsRole ? 'Yes' : 'No'}
          </li>
          <li>Meets Registration: {entry.eligibility.meetsRegistration ? 'Yes' : 'No'}</li>
          <li>Meets Not Won: {entry.eligibility.meetsRequiredNotWon ? 'Yes' : 'No'}</li>
        </ul>
      </div>
    </div>
  );
};

const ReservationSummary: FC<{ readonly reservation: HotelReservation }> = ({ reservation }) => {
  const { ts } = useTranslation();

  return (
    <div>
      <h4>
        {ts('reservation')}
        {reservation.hotel && <>({reservation.hotel.name})</>}
      </h4>
      <div>
        <b>{ts('fee.status')}</b>: {reservation.status}
      </div>
      <div>
        <b>{ts('has_url')}</b>: {reservation.hasUrl ? 'Yes' : 'No'}
      </div>
      {reservation.deletedAt && (
        <div>
          <b>{ts('deleted_on')}</b>: <DateT value={reservation.deletedAt} />
        </div>
      )}
      {reservation.hotel && (
        <div>
          <b>{ts('hotel')}</b>: {reservation.hotel.name} ({reservation.hotel.id})
        </div>
      )}
      {reservation.room && (
        <div>
          <b>{ts('room')}</b>: {reservation.room.name}
        </div>
      )}
      {reservation.guests && reservation.guests.length > 0 && (
        <div>
          <b>{ts('guests')}</b>:{' '}
          {reservation.guests.map((g) => `${g.firstName} ${g.lastName}`).join(', ')}
        </div>
      )}
      {reservation.checkIn && (
        <div>
          <b>{ts('checkin')}</b>: <DateT value={reservation.checkIn} />
        </div>
      )}
      {reservation.checkOut && (
        <div>
          <b>{ts('checkout')}</b>: <DateT value={reservation.checkOut} />
        </div>
      )}
    </div>
  );
};
